// CPLEX Sources panel — shows the state of the periodic input refresh
// from Adam's sales-site /ss/pyomo endpoint. Rendered inside
// ModelDetailDialog so PJ can see whether the cron is feeding the model
// fresh CSVs (and which file, if any, last failed).

import { useCallback, useEffect, useState } from 'react';

import {
  forceInputRefresh,
  getLatestInputBatch,
  listInputBatches,
  type CplexInputBatch,
  type CplexInputBatchDetail,
  type CplexInputBatchState,
  type CplexInputFetchState,
} from '../services/cplex';
import { formatTime } from './CplexAdminPage.helpers';
import styles from './CplexAdminPage.module.css';

function batchStateColor(state: CplexInputBatchState): string {
  switch (state) {
    case 'promoted':
      return '#16a34a'; // green
    case 'unchanged':
      return '#64748b'; // slate
    case 'aborted':
      return '#dc2626'; // red
    case 'fetching':
    case 'pending':
      return '#2563eb'; // blue
    default:
      return '#64748b';
  }
}

function fetchStateColor(state: CplexInputFetchState): string {
  switch (state) {
    case 'fetched':
      return '#16a34a';
    case 'empty':
    case 'http_error':
    case 'parse_failed':
      return '#dc2626';
    default:
      return '#64748b';
  }
}

function fetchStateLabel(state: CplexInputFetchState): string {
  switch (state) {
    case 'fetched':
      return 'OK';
    case 'empty':
      return 'EMPTY';
    case 'http_error':
      return 'HTTP ERR';
    case 'parse_failed':
      return 'PARSE ERR';
    default:
      return state.toUpperCase();
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const ms = Date.now() - t;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

export function CplexSourcesPanel({ modelId }: { modelId: string }) {
  const [latest, setLatest] = useState<CplexInputBatchDetail | null>(null);
  const [history, setHistory] = useState<CplexInputBatch[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [l, h] = await Promise.all([
        getLatestInputBatch(modelId),
        listInputBatches(modelId, { limit: 10 }),
      ]);
      setLatest(l);
      setHistory(h.items);
    } catch (e) {
      const message = (e as { response?: { data?: { detail?: string } }; message?: string });
      setErr(
        message?.response?.data?.detail ??
          message?.message ??
          'Failed to load Sources.',
      );
    }
  }, [modelId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onForceRefresh = useCallback(async () => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const result = await forceInputRefresh(modelId);
      if (!result.enqueued) {
        setInfo(result.reason ?? 'Refresh skipped.');
      } else {
        setInfo(`Refresh enqueued. Task id: ${result.task_id ?? 'unknown'}`);
      }
      await load();
    } catch (e) {
      const message = (e as { response?: { data?: { detail?: string } }; message?: string });
      setErr(
        message?.response?.data?.detail ??
          message?.message ??
          'Force refresh failed.',
      );
    } finally {
      setBusy(false);
    }
  }, [modelId, load]);

  return (
    <div>
      <h3>Input refresh (cron from sales site)</h3>
      {!latest ? (
        <p className={styles.muted}>
          No refresh has run yet for this model. Once a batch lands you'll see
          its outcome and per-file breakdown here.
        </p>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 999,
                background: batchStateColor(latest.state),
                color: 'white',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {latest.state}
            </span>
            <span className={styles.muted}>
              latest_id <code>{latest.latest_id ?? '—'}</code>
              {latest.previous_latest_id && (
                <>
                  {' '}
                  (prev <code>{latest.previous_latest_id}</code>)
                </>
              )}
            </span>
            <span className={styles.muted}>
              started {relativeTime(latest.started_at)} via{' '}
              <code>{latest.trigger}</code>
            </span>
            <span className={styles.muted}>
              {latest.fetched_count} fetched / {latest.failed_count} failed
            </span>
            <button
              className={`${styles.btn} ${styles.small}`}
              disabled={busy}
              onClick={onForceRefresh}
            >
              {busy ? 'Refreshing…' : 'Force refresh'}
            </button>
          </div>
          {latest.error_message && (
            <p className={styles.error} style={{ marginTop: 0 }}>
              {latest.error_message}
            </p>
          )}
          {info && (
            <p className={styles.muted} style={{ marginTop: 0 }}>
              {info}
            </p>
          )}
          {err && <p className={styles.error}>{err}</p>}

          <h4 style={{ marginTop: 18 }}>Files in last batch</h4>
          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
            }}
          >
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>file</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>state</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>rows</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>bytes</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>error</th>
                </tr>
              </thead>
              <tbody>
                {latest.fetches.map((f) => (
                  <tr key={f.filename} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                      {f.filename}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <span
                        style={{
                          color: fetchStateColor(f.state),
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      >
                        {fetchStateLabel(f.state)}
                      </span>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {f.row_count ?? '—'}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {f.size_bytes ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '4px 8px',
                        color: '#dc2626',
                        maxWidth: 360,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={f.parse_error ?? ''}
                    >
                      {f.parse_error ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 style={{ marginTop: 18 }}>Recent batches</h4>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>started</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>state</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>trigger</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>latest_id</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>fetched</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>failed</th>
              </tr>
            </thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '4px 8px' }}>{formatTime(b.started_at)}</td>
                  <td style={{ padding: '4px 8px' }}>
                    <span style={{ color: batchStateColor(b.state), fontWeight: 600 }}>
                      {b.state}
                    </span>
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <code>{b.trigger}</code>
                  </td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                    {b.latest_id ?? '—'}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                    {b.fetched_count}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                    {b.failed_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default CplexSourcesPanel;
